<script lang="ts">
  import { iconClose, iconSettings } from '../icons/index';
  import { t } from '../i18n/index';
  import { onMount } from 'svelte';

  let {
    lang = 'en',
    activeCount = 0,
    onclose,
    onsettings,
    ondrag,
    panelEl = null,
  }: {
    lang?: string;
    activeCount?: number;
    onclose: () => void;
    onsettings: () => void;
    ondrag: (x: number) => void;
    panelEl?: HTMLElement | null;
  } = $props();

  let dragHandleEl: HTMLElement | null = $state(null);

  onMount(() => {
    const handle = dragHandleEl;
    if (!handle) return;

    let dragStartX = 0;
    let startLeft = 0;
    let hasMoved = false;
    let pointerId: number | null = null;

    function onDown(e: PointerEvent) {
      const panel = panelEl;
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      dragStartX = e.clientX;
      startLeft = rect.left;
      hasMoved = false;
      pointerId = e.pointerId;
      handle!.setPointerCapture(e.pointerId);
      e.preventDefault();
    }

    function onMove(e: PointerEvent) {
      if (pointerId === null || e.pointerId !== pointerId) return;
      const panel = panelEl;
      if (!panel) return;

      const dx = e.clientX - dragStartX;
      if (!hasMoved && Math.abs(dx) > 5) {
        hasMoved = true;
        panel.style.transition = 'none';
        // Angedockt ist nur die kanten-abgewandte Seite gerundet (20px 0 0 20px /
        // 0 20px 20px 0) — die kanten-zugewandte Seite ist bündig & eckig. Sobald
        // das Panel von der Kante weggezogen wird, floatet es frei → alle vier
        // Ecken runden, sonst sehen die nun freistehenden Kanten abgeschnitten aus.
        panel.style.borderRadius = '20px';
      }

      if (hasMoved) {
        const panelW = panel.offsetWidth;
        const newLeft = Math.max(0, Math.min(window.innerWidth - panelW, startLeft + dx));
        panel.style.left = newLeft + 'px';
        panel.style.right = 'auto';
      }
    }

    function onUp(e: PointerEvent) {
      if (pointerId === null || e.pointerId !== pointerId) return;
      handle!.releasePointerCapture(e.pointerId);
      const panel = panelEl;
      if (panel) {
        panel.style.transition = '';
        if (hasMoved) {
          const rect = panel.getBoundingClientRect();
          ondrag(rect.left);
        }
      }
      pointerId = null;
      hasMoved = false;
    }

    handle.addEventListener('pointerdown', onDown);
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);

    return () => {
      handle.removeEventListener('pointerdown', onDown);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
    };
  });
</script>

<!--
  R9-3: this used to be a `<header>` element, which implicitly maps to the
  `banner` landmark. Because the widget root already declares
  `role="complementary"` on the shadow host, the nested banner triggered
  `landmark-banner-is-top-level` in axe. Switching to a `<div role="group">`
  drops the banner mapping while keeping a meaningful group for the
  panel-action buttons. The visually-hidden `<h2>` below still labels the
  panel for AT users.
-->
<div class="accessify-header" role="group" aria-labelledby="accessify-panel-title">
  <!-- Semantic panel heading for screen readers. The visible header is the
       Accessify wordmark SVG below; without a real <h2> here, AT users land
       in the panel without a recognizable anchor point. The visual brand
       lives in the <span class="accessify-logo"> next to it. -->
  <h2 id="accessify-panel-title" class="sr-only">{t('widget.title', lang)}</h2>
  <div class="accessify-header-left">
    <!-- Brand wordmark as REAL VECTOR GLYPHS (the 9 "accessify" letter
         outlines from the canonical brand SVG, public/brand/logo-gradient.svg).
         It used to be an SVG <text> with font-family "Inter Display", but the
         widget never loads that font (injectBrandFonts is a no-op since R15-3 —
         Fontshare CORS/CSP killed the external inject), so the wordmark fell
         back to the host system font and looked wrong. Vector paths are
         font-independent → the brand mark is always pixel-correct.

         ONE copy of the glyphs ships (gradient via mask). pickLogoVariant()
         in widget-styles.ts decides per panel-surface luminance: light
         surface keeps the slate→mauve→coral gradient; on a dark brand surface
         the indigo gradient start would vanish, so it flips the SAME mark to a
         solid white silhouette with a CSS `filter: brightness(0) invert(1)` —
         no second copy of the paths needed (keeps the bundle lean). -->
    <span class="accessify-logo" aria-hidden="true">
      <svg viewBox="64 -1503 8551.89 1921" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="accessify-wordmark-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stop-color="#3d4775"/>
            <stop offset="28%"  stop-color="#3d4775"/>
            <stop offset="70%"  stop-color="#8f6a82"/>
            <stop offset="100%" stop-color="#c2473d"/>
          </linearGradient>
          <mask id="accessify-wordmark-mask">
            <rect x="64" y="-1503" width="8552" height="1921" fill="black"/>
            <g transform="scale(1, -1)" fill="white">
              <g transform="translate(0.00, 0)"><path d="M427 -18C589 -18 680 46 736 149H740V0H1028V716C1028 942 862 1078 573 1078C283 1078 111 940 98 722H375C382 803 454 859 564 859C672 859 737 805 737 727V720C737 652 669 643 476 624C256 604 64 536 64 298C64 87 216 -18 427 -18ZM509 187C410 187 346 231 346 306C346 390 426 429 527 444C622 459 705 473 739 492V386C739 272 651 187 509 187Z"/></g>
              <g transform="translate(1075.11, 0)"><path d="M588 -24C869 -24 1071 153 1084 397H797C778 286 711 214 592 214C444 214 355 335 355 527C355 720 443 842 592 842C710 842 784 769 799 661H1084C1065 907 866 1080 588 1080C271 1080 58 854 58 527C58 200 270 -24 588 -24Z"/></g>
              <g transform="translate(2155.22, 0)"><path d="M588 -24C869 -24 1071 153 1084 397H797C778 286 711 214 592 214C444 214 355 335 355 527C355 720 443 842 592 842C710 842 784 769 799 661H1084C1065 907 866 1080 588 1080C271 1080 58 854 58 527C58 200 270 -24 588 -24Z"/></g>
              <g transform="translate(3235.33, 0)"><path d="M586 -24C842 -24 1046 122 1085 332H816C789 250 710 197 595 197C440 197 350 298 345 454H1096V533C1096 855 887 1080 576 1080C273 1080 58 849 58 527C58 206 266 -24 586 -24ZM347 642C363 774 450 856 582 856C715 856 803 774 818 642Z"/></g>
              <g transform="translate(4328.44, 0)"><path d="M543 -26C819 -26 1011 112 1011 323C1011 478 913 570 700 612L500 651C419 668 372 703 372 757C372 820 433 869 534 869C638 869 710 806 712 721H985C979 941 799 1078 526 1078C251 1078 78 946 78 746C78 582 187 478 392 438L577 402C663 385 714 352 714 296C714 231 647 185 538 185C423 185 355 240 344 332H54C72 105 269 -26 543 -26Z"/></g>
              <g transform="translate(5336.56, 0)"><path d="M543 -26C819 -26 1011 112 1011 323C1011 478 913 570 700 612L500 651C419 668 372 703 372 757C372 820 433 869 534 869C638 869 710 806 712 721H985C979 941 799 1078 526 1078C251 1078 78 946 78 746C78 582 187 478 392 438L577 402C663 385 714 352 714 296C714 231 647 185 538 185C423 185 355 240 344 332H54C72 105 269 -26 543 -26Z"/></g>
              <g transform="translate(6344.67, 0)"><path d="M104 0H399V1056H104ZM251 1189C348 1189 419 1256 419 1346C419 1436 348 1503 251 1503C154 1503 83 1436 83 1346C83 1256 154 1189 251 1189Z"/></g>
              <g transform="translate(6790.78, 0)"><path d="M719 1056H503V1143C503 1227 534 1259 619 1259H719V1490H552C324 1490 209 1390 209 1193V1056H18V825H209V0H503V825H719Z"/></g>
              <g transform="translate(7492.89, 0)"><path d="M130 -418H325C489 -418 591 -339 658 -163L1123 1056H817L639 534C614 459 591 382 568 306C546 382 522 458 498 534L321 1056H12L418 4L389 -73C361 -154 334 -185 264 -185H130Z"/></g>
            </g>
          </mask>
        </defs>
        <rect x="64" y="-1503" width="8552" height="1921" fill="url(#accessify-wordmark-gradient)" mask="url(#accessify-wordmark-mask)"/>
      </svg>
    </span>
  </div>

  <div class="accessify-header-actions">
    <!-- Drag handle for free horizontal positioning. R10-8: the icon alone
         was unclear ("what does this cross do?"); aria-label is mandatory
         AND we add a native title tooltip so sighted mouse users get a
         hint on hover too. -->
    <button
      type="button"
      class="accessify-header-btn accessify-drag-handle"
      aria-label={t('ui.movePanel', lang)}
      title={t('ui.movePanelHint', lang)}
      bind:this={dragHandleEl}
    >
      <!-- UntitledUI Move icon (4-arrow cross) — clearer than dot grid -->
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="m5 9-3 3m0 0 3 3m-3-3h20M9 5l3-3m0 0 3 3m-3-3v20m3-3-3 3m0 0-3-3M19 9l3 3m0 0-3 3"/>
      </svg>
    </button>

    <button
      type="button"
      class="accessify-header-btn"
      data-action="settings"
      aria-label={t('ui.settings', lang)}
      title={t('ui.settings', lang)}
      onclick={onsettings}
    >
      {@html iconSettings()}
    </button>

    <!-- Schließen. BEWUSST der auffälligste Button im Header: er hatte vorher
         `background/border: transparent` und war damit als einziger rahmenlos —
         also ausgerechnet die wichtigste Aktion war am schwersten zu finden.
         Jetzt eigene Fläche + Rahmen + größerer Treffer (siehe
         .accessify-header-btn--close in widget-styles.ts) + title-Tooltip.

         Der frühere Reset-Knopf (Kreispfeil) saß hier daneben und wurde
         entfernt: das Icon war nicht selbsterklärend. Zurücksetzen gibt es
         weiterhin verständlich als BESCHRIFTETEN Button in der Statuszeile
         ("Alles zurücksetzen", erscheint sobald etwas aktiv ist) und in den
         Einstellungen; einzelne Funktionen schaltet man ohnehin über die
         großen Feature-Buttons wieder aus. -->
    <button
      type="button"
      class="accessify-header-btn accessify-header-btn--icon accessify-header-btn--close"
      aria-label={t('widget.close', lang)}
      title={t('widget.close', lang)}
      onclick={onclose}
    >
      {@html iconClose()}
    </button>
  </div>
</div>
